import type { OuraResilience } from "../data/ouraTypes";

import { core } from "@tauri-apps/api";
import { useState, useEffect, useCallback } from "react";

import { debugHealth } from "@/domains/logging/utils/debugLog";

/**
 * Optimized Resilience History Hook
 *
 * Fetches historical resilience data for chart visualization
 *
 * @param days - Number of days to fetch history for (default: 30)
 *
 * @returns Object containing resilience history data, loading state, error, and refetch function
 *
 */
export const useOptimizedResilienceHistory = (days = 30) => {
  const [resilienceHistory, setResilienceHistory] = useState<OuraResilience[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  const fetchResilienceHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);

      debugHealth("info", "useOptimizedResilienceHistory", "Fetching resilience history", {
        days,
        endDate: new Date().toISOString().split("T")[0],
        startDate: new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      });

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - days);

      const result = await core.invoke<OuraResilience[]>("get_oura_resilience_history", {
        endDate: endDate.toISOString().split("T")[0],
        startDate: startDate.toISOString().split("T")[0],
      });

      debugHealth("info", "useOptimizedResilienceHistory", "Resilience history fetched", {
        dataPoints: result.length,
        firstDate: result[0]?.day,
        lastDate: result.at(-1)?.day,
        sampleData: result.slice(0, 3),
      });

      setResilienceHistory(result);
    } catch (error_) {
      const errorMessage = error_ instanceof Error ? error_.message : "Unknown error";
      setError(errorMessage);
      debugHealth("error", "useOptimizedResilienceHistory", "Failed to fetch resilience history", {
        days,
        error: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void fetchResilienceHistory();
  }, [fetchResilienceHistory]);

  return {
    error,
    loading,
    refetch: fetchResilienceHistory,
    resilienceHistory,
  };
};
